import { PakistanMap } from "../brand/pakistan-map.js";

import type { CSSProperties } from "react";

/**
 * Transition33 authenticated welcome — the first screen a Government-of-Pakistan
 * guest sees after signing in. A full-viewport "survey plate" overlay (fixed so
 * it is not clipped by the portal shell header/footer): left panel carries
 * the wordmark + statement + "Enter the portal" CTA; the right plate is a
 * cartographic Pakistan map. `onEnter` dismisses it into the portal home.
 *
 * Implements the Claude Design "Transition33 WelcomeScreen" handoff
 * (`docs/Transition33 Welcome Screen (standalone).html`). The layout mirrors the
 * public pre-auth welcome (`routes/portal-public/welcome.tsx`) so the two read as
 * one brand; the visual difference is the **T33 palette** — cream page, deep
 * teal, gold accent — applied here by overriding the portal's semantic
 * design-tokens on the scoped wrapper (`T33_PALETTE`), so the whole surface
 * recolours without touching the global theme. The WelcomeScreen uses Inter +
 * JetBrains Mono only (no Fraunces).
 *
 * @see docs/handover-* project_gop_brand — Transition33 (teal-800/gold-500/cream)
 */

// T33 brand palette (Latitude30 design tokens), mapped onto the portal's
// semantic custom-properties so the existing token-driven markup recolours.
const T33_PALETTE = {
  "--color-background": "#f8f8f8",
  "--color-surface": "#ffffff",
  "--color-fg": "#111b27",
  "--color-fg-muted": "#313d4d",
  "--color-fg-subtle": "#6e7b8b",
  "--color-brand": "#003b44",
  "--color-accent": "#ca8a04",
  "--color-accent-hover": "#b8902a",
  "--color-border": "#dde2e8",
} as CSSProperties;

const PLATE_BG = "#ffffff";

export function Transition33Welcome({ name, onEnter }: { name?: string; onEnter: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 grid h-dvh max-h-dvh w-full overflow-hidden lg:grid-cols-[minmax(320px,40%)_1fr]"
      style={{
        ...T33_PALETTE,
        backgroundColor: "var(--color-background)",
        color: "var(--color-fg)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <RegistrationMarks />

      {/* ─── Left panel ─── */}
      <div
        className="relative flex min-h-0 flex-col px-6 py-8 sm:px-10 lg:px-14 lg:py-10"
        style={{ minWidth: 0 }}
      >
        <MetaStrip />

        <div className="mb-3 flex flex-col gap-2 sm:mb-4 sm:gap-3">
          <span
            className="font-semibold uppercase"
            style={{
              color: "var(--color-brand)",
              fontSize: 11,
              letterSpacing: "0.18em",
            }}
          >
            The Government of Pakistan Portal
          </span>
          <h1
            className="m-0 font-bold"
            style={{
              fontSize: "clamp(36px, 7.5vh, 72px)",
              lineHeight: 0.92,
              letterSpacing: "-0.04em",
              color: "var(--color-fg)",
            }}
            aria-label="Transition 33"
          >
            Transition<span style={{ color: "var(--color-accent)" }}>33</span>
          </h1>
        </div>

        <div
          aria-hidden="true"
          style={{
            height: 2,
            width: "min(280px, 100%)",
            backgroundColor: "var(--color-accent)",
            marginTop: 4,
            flexShrink: 0,
          }}
        />

        <p
          className="m-0 min-h-0"
          style={{
            fontSize: "clamp(15px, 2.1vh, 18px)",
            lineHeight: 1.45,
            color: "var(--color-fg-muted)",
            marginTop: "clamp(12px, 2.5vh, 24px)",
            maxWidth: 460,
            fontWeight: 400,
          }}
        >
          {name ? `Welcome back, ${name}. ` : ""}A suite of Transition Platforms — Pakistan, the
          first to co-develop and prove a model that other countries will want to adopt. Your shared
          documents, the engagement plan, and secure collaboration with Latitude 30 are inside.
        </p>

        <div className="mt-auto flex flex-wrap items-end justify-between gap-4 pt-6">
          <button
            type="button"
            onClick={onEnter}
            className="inline-flex items-center gap-2.5 focus-visible:outline-2 focus-visible:outline-offset-2"
            style={{
              backgroundColor: "var(--color-brand)",
              color: "var(--color-background)",
              padding: "12px 20px",
              border: "none",
              cursor: "pointer",
              fontFamily: "var(--font-sans)",
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "0.04em",
              outlineColor: "var(--color-accent)",
            }}
          >
            Enter the portal
            <span
              style={{ fontFamily: "var(--font-mono)", color: "var(--color-accent)" }}
              aria-hidden="true"
            >
              →
            </span>
          </button>

          <div
            className="text-right uppercase"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.14em",
              color: "var(--color-fg-subtle)",
            }}
          >
            <div>Latitude 30 · Government of Pakistan</div>
            <div style={{ marginTop: 4 }}>Islamabad — London</div>
          </div>
        </div>
      </div>

      {/* ─── Right plate: cartographic Pakistan map ─── */}
      <div
        className="relative hidden min-h-0 lg:block"
        style={{ borderLeft: "1px solid var(--color-border)", backgroundColor: PLATE_BG }}
      >
        {/* eslint-disable-next-line l30/no-inline-svg-icon -- decorative graticule, not iconography (canon 08-design/iconography.md exception) */}
        <svg
          aria-hidden="true"
          className="absolute inset-0"
          width="100%"
          height="100%"
          preserveAspectRatio="none"
        >
          <defs>
            <pattern id="t33aw-grid-minor" width={40} height={40} patternUnits="userSpaceOnUse">
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="rgba(0,56,68,0.10)"
                strokeWidth={0.5}
              />
            </pattern>
            <pattern id="t33aw-grid-major" width={200} height={200} patternUnits="userSpaceOnUse">
              <path
                d="M 200 0 L 0 0 0 200"
                fill="none"
                stroke="rgba(0,56,68,0.22)"
                strokeWidth={0.5}
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#t33aw-grid-minor)" />
          <rect width="100%" height="100%" fill="url(#t33aw-grid-major)" />
        </svg>

        <div className="absolute inset-0" style={{ color: "var(--color-brand)" }}>
          <PakistanMap
            fill="currentColor"
            showLat33
            showIslamabad
            islamabadLabel
            showCities
            showOtherParallels
            style={{ width: "100%", height: "100%" }}
          />
        </div>

        <CoordinateAnnotations />
      </div>
    </div>
  );
}

// ─── Sub-components (mirror the public welcome, T33-scoped) ──────────────────

function RegistrationMarks() {
  const positions: CSSProperties[] = [
    { top: 12, left: 12 },
    { top: 12, right: 12 },
    { bottom: 12, left: 12 },
    { bottom: 12, right: 12 },
  ];
  return (
    <>
      {positions.map((pos, i) => (
        // eslint-disable-next-line l30/no-inline-svg-icon -- decorative registration crosshair (canon 08-design/iconography.md exception)
        <svg
          key={i}
          aria-hidden="true"
          width={12}
          height={12}
          className="pointer-events-none absolute"
          style={pos}
        >
          <line x1={6} y1={0} x2={6} y2={12} stroke="var(--color-brand)" strokeWidth={0.5} />
          <line x1={0} y1={6} x2={12} y2={6} stroke="var(--color-brand)" strokeWidth={0.5} />
        </svg>
      ))}
    </>
  );
}

function MetaStrip() {
  const items: ReadonlyArray<{ caption: string; label: string }> = [
    { caption: "PLATE 01", label: "PORTAL" },
    { caption: "SCALE", label: "1 : 9 600 000" },
    { caption: "PROJ.", label: "WGS 84" },
  ];
  return (
    <div
      className="mb-6 flex shrink-0 gap-8 uppercase sm:mb-8 sm:gap-10 lg:mb-10"
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        letterSpacing: "0.14em",
        color: "var(--color-fg-subtle)",
      }}
    >
      {items.map((item) => (
        <div key={item.caption}>
          <div style={{ color: "var(--color-brand)", marginBottom: 4 }}>{item.caption}</div>
          <div>{item.label}</div>
        </div>
      ))}
    </div>
  );
}

function CoordinateAnnotations() {
  const tag: CSSProperties = {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.15em",
    color: "var(--color-fg-subtle)",
  };
  return (
    <>
      <div className="absolute right-8 top-8 text-right">
        <div style={{ ...tag, color: "var(--color-accent-hover)" }}>33°43′N · 73°05′E</div>
        <div style={{ ...tag, marginTop: 4 }}>Federal Capital Territory</div>
      </div>
      <div className="absolute bottom-8 right-8 text-right">
        <div style={tag}>Islamic Republic of Pakistan</div>
        <div style={{ ...tag, marginTop: 4 }}>881 913 km² · pop. 241M</div>
      </div>
    </>
  );
}
